import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity, Cpu, HardDrive, Thermometer, Clock, Signal, AlertTriangle, CheckCircle, XCircle, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, Search, Router, Edit2, Save, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OLTDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [device, setDevice] = useState(null);
  const [status, setStatus] = useState(null);
  const [onts, setOnts] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [showGE, setShowGE] = useState(false);
  const [selectedPON, setSelectedPON] = useState('all');
  const [ponPortNames, setPonPortNames] = useState({});
  const [showEditPONModal, setShowEditPONModal] = useState(false);
  const [editingPONNames, setEditingPONNames] = useState({});
  const [savingPONNames, setSavingPONNames] = useState(false);
  
  // ONT Customer Name Mapping
  const [showEditONTModal, setShowEditONTModal] = useState(false);
  const [editingONT, setEditingONT] = useState(null);
  const [editingCustomerName, setEditingCustomerName] = useState('');
  const [editingNotes, setEditingNotes] = useState('');
  const [savingONTMapping, setSavingONTMapping] = useState(false);
  
  // Scraping Lock State
  const [isLocked, setIsLocked] = useState(false);
  const [lockMessage, setLockMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  // Handle 423 Locked error
  const handleLockedError = (error) => {
    if (error.response?.status === 423) {
      setIsLocked(true);
      setLockMessage('Monitoring OLT lain sedang berjalan. Mohon tunggu...');
      
      // Auto retry after 5 seconds
      setTimeout(() => {
        if (retryCount < 3) {
          setRetryCount(prev => prev + 1);
          setIsLocked(false);
          window.location.reload(); // Simple reload to retry
        } else {
          setLockMessage('Monitoring masih sibuk. Silakan refresh halaman secara manual.');
        }
      }, 5000);
      
      return true;
    }
    return false;
  };

  // Release lock when component unmounts
  useEffect(() => {
    return () => {
      // Release lock on unmount
      if (token && id) {
        axios.post(`${API_URL}/api/olt/devices/${id}/release-lock`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {
          // Ignore errors on cleanup
        });
      }
    };
  }, [token, id]);

  // Extract PON number from port format
  // Supports multiple formats: "0/1/3", "PON1/1", "1/1", etc.
  const getPonNumber = (ponPort) => {
    if (!ponPort && ponPort !== 0) return null;
    
    // If ponPort is already a number, return it as string
    if (typeof ponPort === 'number') {
      return ponPort.toString();
    }
    
    // Convert to string for regex matching
    const ponPortStr = String(ponPort);
    
    // Try format: "0/1/3" (Slot/PON/ONT)
    let match = ponPortStr.match(/\d+\/(\d+)\/\d+/);
    if (match) return match[1];
    
    // Try format: "PON1/1" or "PON1"
    match = ponPortStr.match(/PON(\d+)/i);
    if (match) return match[1];
    
    // Try format: "1/1" (PON/ONT)
    match = ponPortStr.match(/^(\d+)\/\d+$/);
    if (match) return match[1];
    
    // Try single digit: "1", "2", etc.
    match = ponPortStr.match(/^(\d+)$/);
    if (match) return match[1];
    
    return null;
  };

  const getPonName = (ponPort) => {
    const ponNum = getPonNumber(ponPort);
    if (!ponNum) return null;
    
    // Return custom name if available, otherwise return default "PON{num}"
    return ponPortNames[ponNum] || `PON${ponNum}`;
  };

  // Handle edit PON names
  const handleOpenEditPON = () => {
    // Initialize editing state with current names
    const initialNames = {};
    // Use number_of_pons from device data, fallback to auto-detect from model
    const numPorts = device?.number_of_pons || (device?.model?.includes('04') ? 4 : 8);
    
    for (let i = 1; i <= numPorts; i++) {
      initialNames[i] = ponPortNames[i] || '';
    }
    
    setEditingPONNames(initialNames);
    setShowEditPONModal(true);
  };

  const handleSavePONNames = async () => {
    try {
      setSavingPONNames(true);
      
      // Convert numeric keys to string keys for consistency
      const namesForDB = {};
      Object.keys(editingPONNames).forEach(key => {
        if (editingPONNames[key]?.trim()) {
          namesForDB[key] = editingPONNames[key].trim();
        }
      });
      
      // Update via API
      const response = await axios.put(
        `${API_URL}/api/olt/devices/${id}`,
        { pon_custom_names: namesForDB },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Update local state
        setPonPortNames(namesForDB);
        
        // Update device data
        setDevice(prev => ({
          ...prev,
          pon_custom_names: namesForDB
        }));
        
        toast.success('PON names updated successfully!');
        setShowEditPONModal(false);
      }
    } catch (error) {
      console.error('Error saving PON names:', error);
      toast.error('Failed to save PON names');
    } finally {
      setSavingPONNames(false);
    }
  };

  // Handle edit ONT customer name
  const handleOpenEditONT = (ont) => {
    setEditingONT(ont);
    setEditingCustomerName(ont.name === `ONT-${ont.ont_index}` ? '' : ont.name || '');
    setEditingNotes(ont.notes || '');
    setShowEditONTModal(true);
  };

  const handleSaveONTMapping = async () => {
    if (!editingONT) return;
    
    try {
      setSavingONTMapping(true);
      
      const response = await axios.post(
        `${API_URL}/api/olt/devices/${id}/ont-mappings`,
        {
          pon_port: editingONT.pon_port,
          ont_index: editingONT.ont_index,
          customer_name: editingCustomerName.trim(),
          notes: editingNotes.trim(),
          ont_serial: editingONT.sn || ''
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        toast.success('Customer name saved successfully!');
        setShowEditONTModal(false);
        
        // Refresh ONT list to show updated name
        fetchData();
      }
    } catch (error) {
      console.error('Error saving ONT mapping:', error);
      toast.error('Failed to save customer name');
    } finally {
      setSavingONTMapping(false);
    }
  };

  const getPonPortDisplay = (ponPort) => {
    if (!ponPort) return '-';
    const portName = ponPortNames[ponPort];
    return portName ? `${ponPort} (${portName})` : ponPort;
  };

  // Calculate PON port statistics (group by PON number, not full port)
  const ponPortStats = React.useMemo(() => {
    // Use status.pon_ports from backend if available (more reliable)
    if (status && status.pon_ports && status.pon_ports.length > 0) {
      const stats = {};
      status.pon_ports.forEach(port => {
        stats[port.port_id] = {
          total: port.total_onts || 0,
          online: port.online_onts || 0,
          offline: port.offline_onts || 0,
          los: 0 // Backend doesn't separate los from offline
        };
      });
      return stats;
    }
    
    // Fallback: Calculate from ONT list if backend doesn't provide pon_ports
    const stats = {};
    
    onts.forEach(ont => {
      if (ont.pon_port) {
        let ponNum = getPonNumber(ont.pon_port);
        
        // Fallback: if getPonNumber returns null, try direct extraction
        if (!ponNum) {
          const match = ont.pon_port.match(/(\d+)/);
          ponNum = match ? match[1] : null;
        }
        
        if (ponNum) {
          if (!stats[ponNum]) {
            stats[ponNum] = { total: 0, online: 0, offline: 0, los: 0 };
          }
          stats[ponNum].total++;
          if (ont.status === 'online') stats[ponNum].online++;
          else if (ont.status === 'offline') stats[ponNum].offline++;
          else if (ont.status === 'los') stats[ponNum].los++;
        }
      }
    });
    
    return stats;
  }, [onts, status]);

  const totalPonPorts = Object.keys(ponPortStats).length;

  // Load from localStorage on mount (instant display)
  useEffect(() => {
    if (id) {
      try {
        const cached = localStorage.getItem(`olt_data_${id}`);
        if (cached) {
          const data = JSON.parse(cached);
          const cacheAge = Date.now() - new Date(data.timestamp).getTime();
          
          // Don't use cache if it has error status - always fetch fresh
          if (data.status && data.status.status === 'error') {
            console.log('Cached error state detected - clearing cache and fetching fresh');
            localStorage.removeItem(`olt_data_${id}`);
          }
          // Use cache if less than 5 minutes old and not error
          else if (cacheAge < 5 * 60 * 1000) {
            if (data.status) setStatus(data.status);
            if (data.onts) setOnts(data.onts);
            if (data.alarms) setAlarms(data.alarms);
            console.log('Loaded data from localStorage (instant)');
          }
        }
      } catch (err) {
        console.error('Error loading from localStorage:', err);
        // Clear corrupted cache
        localStorage.removeItem(`olt_data_${id}`);
      }
    }
  }, [id]);

  useEffect(() => {
    if (token) {
      // Load cached data first (instant), then fetch fresh data
      fetchData(false, true); // (isAutoRefresh=false, useCacheFirst=true)
      // No polling - OLT detail is refreshed on-demand by user
    }
  }, [token, id]);

  const fetchData = async (isAutoRefresh = false, useCacheFirst = false) => {
    try {
      if (!isAutoRefresh && !useCacheFirst) {
        setLoading(true);
      } else if (!useCacheFirst) {
        setRefreshing(true);
      }

      // If useCacheFirst, load data immediately then fetch fresh in background
      if (useCacheFirst) {
        setLoading(true);
        
        // Load device info first (fast)
        try {
          const deviceRes = await axios.get(`${API_URL}/api/olt/devices/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const deviceData = deviceRes.data.data;
          setDevice(deviceData);
          
          // Extract custom PON names from device data
          if (deviceData.pon_custom_names) {
            setPonPortNames(deviceData.pon_custom_names);
          }
          
          setLoading(false);
        } catch (err) {
          console.error('Error loading device:', err);
        }
        
        // Fetch other data in background (show loading indicator but allow interaction)
        setRefreshing(true);
        setTimeout(async () => {
          try {
            const [statusRes, ontsRes, alarmsRes] = await Promise.all([
              axios.get(`${API_URL}/api/olt/devices/${id}/status`, {
                headers: { Authorization: `Bearer ${token}` }
              }),
              axios.get(`${API_URL}/api/olt/devices/${id}/onts`, {
                headers: { Authorization: `Bearer ${token}` }
              }),
              axios.get(`${API_URL}/api/olt/devices/${id}/alarms`, {
                headers: { Authorization: `Bearer ${token}` }
              })
            ]);

            // Ensure we have data before setting state
            if (statusRes && statusRes.data && statusRes.data.data) {
              setStatus(statusRes.data.data);
            }
            if (ontsRes && ontsRes.data && ontsRes.data.data) {
              setOnts(ontsRes.data.data);
            }
            if (alarmsRes && alarmsRes.data && alarmsRes.data.data) {
              setAlarms(alarmsRes.data.data);
            }
            setLastRefresh(new Date());
            
            // Save to localStorage for instant load next time
            try {
              localStorage.setItem(`olt_data_${id}`, JSON.stringify({
                status: statusRes.data.data,
                onts: ontsRes.data.data,
                alarms: alarmsRes.data.data,
                timestamp: new Date().toISOString()
              }));
            } catch (err) {
              console.error('Error saving to localStorage:', err);
            }
            
            console.log('Background data fetch completed');
          } catch (err) {
            console.error('Error in background fetch:', err);
            
            // Handle scraping lock
            if (handleLockedError(err)) {
              return;
            }
            
            // Set empty status to prevent infinite loading
            setStatus({
              total_onts: 0,
              online_onts: 0,
              offline_onts: 0,
              pon_ports: [],
              status: 'error'
            });
          } finally {
            setRefreshing(false);
          }
        }, 100);
      } else {
        // Normal fetch - wait for all data
        const [deviceRes, statusRes, ontsRes, alarmsRes] = await Promise.all([
          axios.get(`${API_URL}/api/olt/devices/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/olt/devices/${id}/status`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/olt/devices/${id}/onts`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/olt/devices/${id}/alarms`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        const deviceData = deviceRes.data.data;
        setDevice(deviceData);
        
        // Extract custom PON names
        if (deviceData.pon_custom_names) {
          setPonPortNames(deviceData.pon_custom_names);
        }
        
        // Ensure we have valid data before setting state
        if (statusRes && statusRes.data && statusRes.data.data) {
          setStatus(statusRes.data.data);
        }
        if (ontsRes && ontsRes.data && ontsRes.data.data) {
          setOnts(ontsRes.data.data);
        }
        if (alarmsRes && alarmsRes.data && alarmsRes.data.data) {
          setAlarms(alarmsRes.data.data);
        }
        setLastRefresh(new Date());
        
        // Save to localStorage for instant load next time
        try {
          localStorage.setItem(`olt_data_${id}`, JSON.stringify({
            status: statusRes.data.data,
            onts: ontsRes.data.data,
            alarms: alarmsRes.data.data,
            timestamp: new Date().toISOString()
          }));
        } catch (err) {
          console.error('Error saving to localStorage:', err);
        }
        
        if (isAutoRefresh) {
          console.log('Auto-refreshed OLT data');
        }
        
        setLoading(false);
        setRefreshing(false);
      }
    } catch (error) {
      console.error('Error fetching OLT data:', error);
      
      // Handle scraping lock
      if (handleLockedError(error)) {
        setLoading(false);
        setRefreshing(false);
        return;
      }
      
      if (!isAutoRefresh) {
        toast.error('Gagal memuat data OLT');
      }
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    fetchData();
    toast.success('Memuat ulang data...');
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 ml-1 text-slate-400 dark:text-slate-500" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />
      : <ArrowDown className="w-3 h-3 ml-1 text-indigo-600 dark:text-indigo-400" />;
  };

  // Filter ONTs based on search query and selected PON
  const filteredOnts = React.useMemo(() => {
    let filtered = onts;
    
    // Filter by selected PON
    if (selectedPON !== 'all') {
      filtered = filtered.filter(ont => {
        const ponNum = getPonNumber(ont.pon_port);
        return ponNum === selectedPON;
      });
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ont => {
        const ponName = getPonName(ont.pon_port);
        return (
          ont.serial_number?.toLowerCase().includes(query) ||
          ont.sn?.toLowerCase().includes(query) ||
          ont.name?.toLowerCase().includes(query) ||
          ont.customer_id?.toLowerCase().includes(query) ||
          ont.notes?.toLowerCase().includes(query) ||
          ont.pon_port?.toLowerCase().includes(query) ||
          ponName?.toLowerCase().includes(query) ||
          ont.status?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered;
  }, [onts, searchQuery, selectedPON]);

  const sortedOnts = React.useMemo(() => {
    if (!sortConfig.key) return filteredOnts;
    
    return [...filteredOnts].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle special sorting for pon_port (extract PON number from 0/PON/ONT format)
      if (sortConfig.key === 'pon_port') {
        const aPort = aVal ? parseInt(aVal.match(/\d+\/(\d+)\/\d+/)?.[1] || '0') : 0;
        const bPort = bVal ? parseInt(bVal.match(/\d+\/(\d+)\/\d+/)?.[1] || '0') : 0;
        return sortConfig.direction === 'asc' ? aPort - bPort : bPort - aPort;
      }
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredOnts, sortConfig]);

  // Pagination
  const paginatedOnts = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedOnts.slice(startIndex, endIndex);
  }, [sortedOnts, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedOnts.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds || seconds === 0) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const formatLastRefresh = () => {
    if (!lastRefresh) return '';
    const now = new Date();
    const diffSeconds = Math.floor((now - lastRefresh) / 1000);
    if (diffSeconds < 60) return `${diffSeconds}s yang lalu`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    return `${diffMinutes}m yang lalu`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      online: 'bg-green-100 text-green-700',
      offline: 'bg-red-100 text-red-700',
      los: 'bg-yellow-100 text-yellow-700'
    };
    return (
      <span className={`px-2 py-1 rounded-md text-xs font-semibold ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
        {status}
      </span>
    );
  };

  const getSignalColor = (signal) => {
    if (!signal) return 'text-slate-400';
    if (signal >= -20) return 'text-green-600';
    if (signal >= -25) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      warning: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      info: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return colors[severity] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 dark:text-indigo-400" />
      </div>
    );
  }

  if (!device) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500 dark:text-slate-400">OLT device tidak ditemukan</p>
        <Button onClick={() => navigate('/app/olt')} className="mt-4">
          Kembali ke Daftar OLT
        </Button>
      </div>
    );
  }

  // Show minimal UI if status is still loading
  if (!status) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-4">
          <Button onClick={() => navigate('/app/olt')} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{device.name}</h1>
            <p className="text-sm text-slate-600 dark:text-slate-300">{device.vendor} {device.model} • {device.ip_address}</p>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-2 dark:text-indigo-400" />
            <p className="text-sm text-slate-600 dark:text-slate-300">Loading OLT status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <Button
          onClick={() => navigate('/app/olt')}
          variant="outline"
          size="sm"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{device.name}</h1>
            {refreshing && <RefreshCw className="w-4 h-4 animate-spin text-indigo-600 dark:text-indigo-400" />}
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
            <span>{device.vendor} {device.model} • {device.ip_address}</span>
            {lastRefresh && (
              <span className="text-xs text-slate-500 dark:text-slate-400">• Diperbarui {formatLastRefresh()}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={autoRefreshEnabled}
              onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (60s)
          </label>
          <Button
            onClick={handleManualRefresh}
            size="sm"
            variant="outline"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
        <Card className="shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <div className="text-xs font-medium text-slate-600 dark:text-slate-300">Status</div>
            </div>
            <div className={`text-lg font-bold ${status.status === 'online' ? 'text-green-600' : 'text-red-600'}`}>
              {status.status}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-slate-600 dark:text-slate-300" />
              <div className="text-xs font-medium text-slate-600 dark:text-slate-300">Uptime</div>
            </div>
            <div className="text-lg font-bold text-slate-900 dark:text-slate-100" title={formatUptime(status.uptime_seconds)}>
              {status.uptime_seconds > 0 ? formatUptime(status.uptime_seconds).split(' ')[0] : 'N/A'}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Cpu className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <div className="text-xs font-medium text-slate-600 dark:text-slate-300">CPU</div>
            </div>
            <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {status.cpu_usage > 0 ? `${status.cpu_usage}%` : 'N/A'}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <HardDrive className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <div className="text-xs font-medium text-slate-600 dark:text-slate-300">Memory</div>
            </div>
            <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {status.memory_usage > 0 ? `${status.memory_usage}%` : 'N/A'}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Thermometer className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <div className="text-xs font-medium text-slate-600 dark:text-slate-300">Temp</div>
            </div>
            <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {status.temperature > 0 ? `${status.temperature}°C` : 'N/A'}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Signal className="w-4 h-4 text-green-600 dark:text-green-400" />
              <div className="text-xs font-medium text-slate-600 dark:text-slate-300">ONTs</div>
            </div>
            <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {onts.filter(o => o.status === 'online').length}/{onts.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PON Port Information - HSGQ Style */}
      <div className="mb-4">
        <Card className="shadow-sm">
          <CardContent className="p-4">
            {/* PON Informations Header */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-cyan-500">PON Informations</h3>
                <Button
                  onClick={handleOpenEditPON}
                  size="sm"
                  variant="outline"
                  className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/40 dark:text-indigo-300"
                >
                  <Router className="w-4 h-4 mr-1" />
                  Edit PON Names
                </Button>
              </div>
              <div className="flex items-center gap-8 text-sm">
                <span className="text-slate-600 dark:text-slate-300">
                  Total: <span className="font-bold text-slate-900 dark:text-slate-100">{onts.length}</span>
                </span>
                <span className="text-slate-600 dark:text-slate-300">
                  Online: <span className="font-bold text-green-600 dark:text-green-400">{onts.filter(o => o.status === 'online').length}</span>
                </span>
                <span className="text-slate-600 dark:text-slate-300">
                  Offline: <span className="font-bold text-red-600 dark:text-red-400">{onts.filter(o => o.status !== 'online').length}</span>
                </span>
              </div>
            </div>

            {/* PON Port Cards - Compact Modern Design */}
            {onts.length > 0 && Object.keys(ponPortStats).length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
                {Object.entries(ponPortStats)
                  .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                  .filter(([ponNum]) => {
                    // Filter based on number_of_pons from database, fallback to model detection
                    const maxPorts = device?.number_of_pons || (() => {
                      const modelMatch = device?.model?.match(/XE0*(\d+)/i);
                      return modelMatch ? parseInt(modelMatch[1]) : 8;
                    })();
                    return parseInt(ponNum) <= maxPorts;
                  })
                  .map(([ponNum, stats]) => {
                  const isOnline = stats.online > 0;
                  const ponName = ponPortNames[ponNum];
                  const onlinePercentage = stats.total > 0 ? Math.round((stats.online / stats.total) * 100) : 0;
                  
                  return (
                    <div key={ponNum} className="bg-white border-2 border-slate-200 rounded-xl p-3 hover:border-blue-400 hover:shadow-lg transition-all duration-200 dark:bg-slate-800 dark:border-slate-700">
                      {/* Compact Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-bold text-slate-700 dark:text-slate-200">PON{ponNum}</div>
                        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`}></div>
                      </div>
                      
                      {/* PON Icon - Smaller & Modern */}
                      <div className="flex items-center justify-center mb-2">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isOnline ? 'bg-gradient-to-br from-blue-400 to-blue-600' : 'bg-gradient-to-br from-slate-300 to-slate-400'} shadow-md`}>
                          <Router className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      
                      {/* Compact Stats */}
                      <div className="space-y-1 mb-2">
                        <div className="flex justify-between text-[11px]">
                          <span className="text-slate-600 dark:text-slate-300">Total</span>
                          <span className="font-bold text-slate-900 dark:text-slate-100">{stats.total}</span>
                        </div>
                        <div className="flex justify-between text-[11px]">
                          <span className="text-slate-600 dark:text-slate-300">Online</span>
                          <span className="font-bold text-green-600 dark:text-green-400">{stats.online}</span>
                        </div>
                        <div className="flex justify-between text-[11px]">
                          <span className="text-slate-600 dark:text-slate-300">Offline</span>
                          <span className="font-bold text-red-600 dark:text-red-400">{stats.offline + stats.los}</span>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mb-2">
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden dark:bg-slate-700">
                          <div 
                            className={`h-full ${isOnline ? 'bg-gradient-to-r from-green-400 to-green-600' : 'bg-slate-300'} transition-all duration-300`}
                            style={{ width: `${onlinePercentage}%` }}
                          ></div>
                        </div>
                        <div className="text-[10px] text-slate-500 text-center mt-0.5 dark:text-slate-400">{onlinePercentage}% Online</div>
                      </div>
                      
                      {/* Port Name - Compact */}
                      <div className="text-center border-t pt-2">
                        <div className="text-[10px] font-semibold text-slate-600 truncate dark:text-slate-300" title={ponName}>
                          {ponName || '-'}
                        </div>
                      </div>
                    </div>
                  );
                  })}
              </div>
            ) : onts.length === 0 ? (
              <div className="text-center py-4 text-sm text-slate-500 dark:text-slate-400">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-indigo-600 dark:text-indigo-400" />
                Loading PON port information...
              </div>
            ) : (
              <div className="text-center py-4 text-sm text-amber-600 bg-amber-50 rounded-lg border border-amber-200 dark:text-amber-400 dark:bg-amber-900/30">
                <p>⚠️ PON port data tidak dapat dikelompokkan</p>
                <p className="text-xs mt-1">Total {onts.length} ONT ditemukan, tapi format PON port tidak standar</p>
              </div>
            )}

            {/* GE Informations - Toggle Show/Hide */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-cyan-500">GE Informations</h3>
                <Button
                  onClick={() => setShowGE(!showGE)}
                  size="sm"
                  variant="outline"
                  className="text-xs"
                >
                  {showGE ? (
                    <>
                      <XCircle className="w-3 h-3 mr-1" />
                      Hide GE
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Show GE
                    </>
                  )}
                </Button>
              </div>
              
              {showGE && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['GE1', 'GE2', 'GE3', 'GE4'].map((ge) => (
                    <div key={ge} className="bg-white border-2 border-slate-200 rounded-xl p-3 hover:border-slate-400 transition-all dark:bg-slate-800 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-bold text-slate-700 dark:text-slate-200">{ge}</div>
                        <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                      </div>
                      
                      <div className="flex items-center justify-center mb-2">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center shadow-md">
                          <Signal className="w-6 h-6 text-slate-500 dark:text-slate-400" />
                        </div>
                      </div>
                      
                      <div className="space-y-1 mb-2">
                        <div className="text-[11px] text-center text-slate-600 font-medium dark:text-slate-300">Offline</div>
                        <div className="text-[10px] text-center text-slate-500 dark:text-slate-400">Admin: Enable</div>
                        <div className="text-[10px] text-center text-slate-500 dark:text-slate-400">Link: Down</div>
                      </div>
                      
                      <div className="h-1.5 bg-slate-200 rounded-full dark:bg-slate-700"></div>
                    </div>
                  ))}
                </div>
              )}
              
              {!showGE && (
                <div className="text-center py-3 text-xs text-slate-500 bg-slate-50 rounded-lg border border-slate-200 dark:text-slate-400 dark:bg-slate-900 dark:border-slate-700">
                  GE Information disembunyikan. Klik "Show GE" untuk menampilkan.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ONTs Table */}
        <div className="lg:col-span-2">
          <Card className="shadow-sm">
            <CardContent className="p-0">
              <div className="p-3 border-b bg-slate-50 dark:bg-slate-900">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-semibold text-slate-900 dark:text-slate-100">ONT/ONU Status</h2>
                  <span className="text-xs text-slate-600 dark:text-slate-300">
                    {filteredOnts.length !== onts.length && `${filteredOnts.length} dari `}{onts.length} ONT
                  </span>
                </div>
                
                {/* Filter by PON Dropdown */}
                <div className="mb-2">
                  <label className="text-xs font-medium text-slate-700 mb-1 block dark:text-slate-200">Filter by PON:</label>
                  <select
                    value={selectedPON}
                    onChange={(e) => {
                      setSelectedPON(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:border-slate-600 dark:bg-slate-800"
                  >
                    <option value="all">Semua PON (All)</option>
                    {Object.entries(ponPortStats)
                      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                      .map(([ponNum, stats]) => (
                        <option key={ponNum} value={ponNum}>
                          PON{ponNum} - {ponPortNames[ponNum] || '-'} ({stats.total} ONT)
                        </option>
                      ))}
                  </select>
                </div>
                
                {/* Search Box */}
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  <Input
                    type="text"
                    placeholder="Cari berdasarkan Serial Number, Customer, PON Port, atau Status..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1); // Reset to first page when searching
                    }}
                    className="pl-9 text-sm"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-slate-500"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Pagination Controls - Top */}
                {sortedOnts.length > 0 && (
                  <div className="mt-3 p-2 border-t border-b bg-slate-50 dark:bg-slate-900">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-600 dark:text-slate-300">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedOnts.length)} of {sortedOnts.length} ONTs
                      </div>
                      <div className="flex items-center gap-2">
                        <select 
                          value={itemsPerPage} 
                          onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          className="text-xs border rounded px-2 py-1"
                        >
                          <option value={25}>25 per page</option>
                          <option value={50}>50 per page</option>
                          <option value={100}>100 per page</option>
                          <option value={sortedOnts.length}>All ({sortedOnts.length})</option>
                        </select>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            className="px-2 py-1 text-xs border rounded disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            ««
                          </button>
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-2 py-1 text-xs border rounded disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            «
                          </button>
                          <span className="px-3 py-1 text-xs border rounded bg-white dark:bg-slate-800">
                            {currentPage} / {totalPages}
                          </span>
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-2 py-1 text-xs border rounded disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            »
                          </button>
                          <button
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            className="px-2 py-1 text-xs border rounded disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            »»
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200 dark:border-slate-700">
                    <tr>
                      <th 
                        className="px-3 py-2 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 dark:text-slate-200"
                        onClick={() => handleSort('serial_number')}
                      >
                        <div className="flex items-center">
                          Serial Number
                          {getSortIcon('serial_number')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-2 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 dark:text-slate-200"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          Status
                          {getSortIcon('status')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-2 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 dark:text-slate-200"
                        onClick={() => handleSort('signal_rx')}
                      >
                        <div className="flex items-center">
                          Signal (dBm)
                          {getSortIcon('signal_rx')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-2 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 dark:text-slate-200"
                        onClick={() => handleSort('pon_port')}
                      >
                        <div className="flex items-center">
                          PON Port
                          {getSortIcon('pon_port')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-2 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 dark:text-slate-200"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center">
                          Customer
                          {getSortIcon('name')}
                        </div>
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {paginatedOnts.map((ont) => (
                      <tr key={ont.id || `${ont.pon_port}-${ont.ont_index}`} className="hover:bg-indigo-50/30 transition-colors">
                        <td className="px-3 py-2 text-xs font-mono text-slate-700 dark:text-slate-200">{ont.sn || ont.serial_number || 'N/A'}</td>
                        <td className="px-3 py-2 text-xs">{getStatusBadge(ont.status)}</td>
                        <td className="px-3 py-2 text-xs">
                          {ont.signal_rx ? (
                            <span className={`font-semibold ${getSignalColor(ont.signal_rx)}`}>
                              {ont.signal_rx} dBm
                            </span>
                          ) : (
                            <span className="text-slate-400 dark:text-slate-500">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-700 dark:text-slate-200">{ont.pon_port}</span>
                            {getPonName(ont.pon_port) && (
                              <span className="text-[10px] text-indigo-600 font-medium dark:text-indigo-400">PON{getPonNumber(ont.pon_port)}: {getPonName(ont.pon_port)}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                          <div className="flex flex-col">
                            <span className={`font-medium ${ont.has_mapping ? 'text-slate-800' : 'text-slate-400'}`}>
                              {ont.name || ont.customer_id || '-'}
                            </span>
                            {ont.notes && (
                              <span className="text-[10px] text-slate-500 italic dark:text-slate-400">{ont.notes}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <button
                            onClick={() => handleOpenEditONT(ont)}
                            className="text-indigo-600 hover:text-indigo-800 transition-colors p-1 hover:bg-indigo-50 rounded dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                            title="Edit customer name"
                          >
                            <Edit2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sortedOnts.length > 0 && (
                <div className="p-3 border-t bg-slate-50 dark:bg-slate-900">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-600 dark:text-slate-300">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedOnts.length)} of {sortedOnts.length} ONTs
                    </div>
                    <div className="flex items-center gap-2">
                      <select 
                        value={itemsPerPage} 
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="text-xs border rounded px-2 py-1"
                      >
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                        <option value={sortedOnts.length}>All ({sortedOnts.length})</option>
                      </select>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                          className="px-2 py-1 text-xs border rounded disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          ««
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-2 py-1 text-xs border rounded disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          «
                        </button>
                        <span className="px-3 py-1 text-xs border rounded bg-white dark:bg-slate-800">
                          {currentPage} / {totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-2 py-1 text-xs border rounded disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          »
                        </button>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                          className="px-2 py-1 text-xs border rounded disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          »»
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Alarms */}
        <div>
          <Card className="shadow-sm">
            <CardContent className="p-0">
              <div className="p-3 border-b bg-slate-50 dark:bg-slate-900">
                <h2 className="font-semibold text-slate-900 dark:text-slate-100">Recent Alarms</h2>
              </div>
              <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
                {alarms.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className="text-sm">No alarms</p>
                  </div>
                ) : (
                  alarms.map((alarm) => (
                    <div
                      key={alarm.id}
                      className={`p-2 rounded-lg border ${getSeverityColor(alarm.severity)}`}
                    >
                      <div className="flex items-start gap-2">
                        {alarm.severity === 'critical' ? (
                          <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-xs">{alarm.type}</div>
                          <div className="text-xs opacity-90 mt-0.5">{alarm.description}</div>
                          <div className="text-xs opacity-75 mt-1">
                            {new Date(alarm.timestamp).toLocaleString('id-ID')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit PON Names Modal */}
      {showEditPONModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Edit PON Port Names</h2>
                <button
                  onClick={() => setShowEditPONModal(false)}
                  className="text-slate-400 hover:text-slate-600 dark:text-slate-500"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">
                Customize the display names for each PON port
              </p>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.keys(editingPONNames).map((ponNum) => (
                  <div key={ponNum} className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                      PON {ponNum}
                    </label>
                    <Input
                      type="text"
                      value={editingPONNames[ponNum]}
                      onChange={(e) => setEditingPONNames(prev => ({
                        ...prev,
                        [ponNum]: e.target.value
                      }))}
                      placeholder={`Enter name for PON${ponNum}`}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 dark:border-slate-700 dark:bg-slate-900">
              <Button
                onClick={() => setShowEditPONModal(false)}
                variant="outline"
                disabled={savingPONNames}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePONNames}
                disabled={savingPONNames}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {savingPONNames ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save PON Names
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit ONT Customer Name Modal */}
      {showEditONTModal && editingONT && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full dark:bg-slate-800">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Edit Customer Name</h2>
                <button
                  onClick={() => setShowEditONTModal(false)}
                  className="text-slate-400 hover:text-slate-600 dark:text-slate-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">
                ONT Index: {editingONT.ont_index} | PON: {editingONT.pon_port}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Customer Name
                </label>
                <Input
                  type="text"
                  value={editingCustomerName}
                  onChange={(e) => setEditingCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                  className="w-full"
                  autoFocus
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Notes (Optional)
                </label>
                <textarea
                  value={editingNotes}
                  onChange={(e) => setEditingNotes(e.target.value)}
                  placeholder="Enter notes (e.g., Warung Makan, Toko Elektronik)"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm dark:border-slate-600"
                  rows={2}
                />
              </div>
              
              {editingONT.sn && editingONT.sn !== 'N/A' && (
                <div className="text-xs text-slate-500 p-3 bg-slate-50 rounded dark:text-slate-400 dark:bg-slate-900">
                  <div><span className="font-medium">Serial Number:</span> {editingONT.sn}</div>
                  <div><span className="font-medium">Status:</span> {editingONT.status}</div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 dark:border-slate-700 dark:bg-slate-900">
              <Button
                onClick={() => setShowEditONTModal(false)}
                variant="outline"
                disabled={savingONTMapping}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveONTMapping}
                disabled={savingONTMapping}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {savingONTMapping ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Customer Name
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Scraping Lock Modal */}
      {isLocked && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 dark:bg-slate-800">
            <div className="flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
            
            <h3 className="text-lg font-semibold text-center text-slate-900 mb-2 dark:text-slate-100">
              Mohon Tunggu
            </h3>
            
            <p className="text-center text-slate-600 mb-4 dark:text-slate-300">
              {lockMessage}
            </p>
            
            {retryCount > 0 && (
              <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                Percobaan ke-{retryCount} dari 3...
              </p>
            )}
            
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => {
                  setIsLocked(false);
                  navigate('/app/olt');
                }}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors dark:text-slate-300 dark:hover:text-slate-100 dark:border-slate-600 dark:hover:bg-slate-700/50"
              >
                Kembali ke List
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OLTDetail;
